import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ImpactBadge, DocTypeBadge, getClassificationInfo, formatDDMMYYYY, formatISODate } from '../components/badges';
import RelatedKnowledge from '../components/RelatedKnowledge';
import { API_BASE_URL } from '../config/api';
import { cleanPostContent } from '../utils/contentCleaner';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import IncidentBanner from '../components/IncidentBanner';
import { usePageMeta } from '../hooks/usePageMeta';
import { ExplainerButton } from '../components/legislation/LegislationExplainer';
import { stationOf } from '../components/legislation/lineModel';

// ── Types ────────────────────────────────────────────────────────────

interface KeyDate {
  date: string;
  description: string;
}

interface EuLawItem {
  id: number;
  created_at: string;
  date_of_document: string | null;
  published_date: string | null;
  post_title: string | null;
  post_content: string | null;
  post_link: string | null;
  document_type: string | null;
  executive_summary: string | null;
  key_dates: KeyDate[] | null;
  affected_products: string[] | null;
  action_required: string | null;
  impact_level: string | null;
  celex_number: string | null;
  regulation?: {
    regulation_number: string | null;
    regulation_name: string | null;
    url_legislation: string | null;
  };
}

interface RasffItem {
  rasff_id: number;
  notif_id: number;
  reference: string | null;
  subject: string | null;
  ec_validation_date: string | null;
  notifying_country: string | null;
  product_category: string | null;
  product_type: string | null;
  classification: string | null;
  risk_decision: string | null;
  hazard_names: string | null;
  dimhazards_id: number | null;
  dim_hazards: {
    dimhazards_id: number;
    hazardname: string;
    hazardscategoryname: string | null;
  } | null;
}

interface FoodAlertItem {
  id: number;
  source: string;
  external_id: string;
  title: string | null;
  description: string | null;
  alert_type: string | null;
  severity: string | null;
  products: string[] | null;
  /** type "description"/"reason" carry text; type "notice" (RappelConso
   *  recall events folded from several fiches) also carries the fiche url. */
  hazards: Array<{ type: string; text: string; url?: string | null; reference?: string | null }> | null;
  countries: string[];
  url: string | null;
  published_date: string | null;
  dimhazards_id: number | null;
  hazard_category: string | null;
  hazard_name: string | null;
}

/** Draft EU act in the legislative pipeline (Comitology Register dossier),
 *  served by GET /api/legislation/pipeline. Dates prefixed expected_* are
 *  derived from the draft text / procedure and are not final until OJ
 *  publication. */
export interface PipelineSource {
  type: 'D' | 'V' | 'S' | 'A' | 'HYS' | 'EURLEX' | string;
  ref: string;
  version: number;
  title: string;
  url: string;
  date: string | null;
  meeting_code: string | null;
  meeting_date: string | null;
  files?: Array<{ role: string | null; language: string | null; url: string; kind: string | null }>;
}

export interface PipelineKeyDate extends KeyDate {
  confidence?: string;
  computed?: boolean;
}

/** A dossier as the pipeline list returns it: enough for the pipeline table, a
 *  closed card and the single-applicant group. A card loads the rest
 *  (PipelineDossier) when it is opened. */
export interface PipelineItem {
  id: number;
  dossier_key: string;
  register_refs: string[];
  plan_ref: string | null;
  title: string | null;
  title_official: string | null;
  act_type: string | null;
  basic_act_ref: string | null;
  section_label: string | null;
  relevance: string | null;
  stage: string;
  stage_date: string | null;
  meeting_date: string | null;
  meeting_code: string | null;
  vote_opinion: string | null;
  legislative_procedure: string | null;
  expected_adoption: string | null;
  expected_publication: string | null;
  expected_application: string | null;
  application_confirmed: boolean;
  celex_number: string | null;
  affected_products: string[] | null;
  impact_level: string | null;
  hazard: { id: number; name: string; category: string | null } | null;
  /** Latest draft document in the Comitology Register. */
  draft_url: string | null;
  last_event_at: string | null;
}

/** One dossier in full, from GET /api/legislation/dossier/:key. */
export interface PipelineDossier extends PipelineItem {
  executive_summary: string | null;
  post_content: string | null;
  key_dates: PipelineKeyDate[] | null;
  action_required: string | null;
  sources: PipelineSource[];
}

interface PipelineGroup {
  meeting_code: string;
  meeting_date: string | null;
  stage: string;
  items: PipelineItem[];
}

interface UnifiedItem {
  id: string;
  sourceType: string;
  title: string;
  sortDate: Date;
  pipeline?: PipelineItem;
  pipelineGroup?: PipelineGroup;
  eulaw?: EuLawItem;
  rasff?: RasffItem;
  alert?: FoodAlertItem;
}

// ── Tab config ────────────────────────────────────────────────────────
// "alerts" groups all national recall feeds; "efsa" is separate (scientific)

const SOURCE_TABS = [
  { key: 'all', label: 'All' },
  { key: 'eulaw', label: 'EU Law' },
  { key: 'rasff', label: 'RASFF' },
  { key: 'alerts', label: 'Alerts' },
  { key: 'efsa', label: 'Science' },
] as const;

type SourceFilter = typeof SOURCE_TABS[number]['key'];

const ALERT_SOURCES = new Set([
  'fsa_uk', 'openfda', 'cfia', 'fsanz', 'fsis',
  'rappelconso', 'lebensmittelwarnung', 'nvwa', 'favv', 'favv_news',
  'fsai', 'aesan',
]);

// Science & surveillance publications (the 'efsa' tab key is kept for URL
// compatibility with old ?tab=efsa deep links).
const SCIENCE_SOURCES = new Set(['efsa', 'ecdc', 'sciensano']);

function matchesFilter(sourceType: string, filter: SourceFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'alerts') return ALERT_SOURCES.has(sourceType);
  if (filter === 'efsa') return SCIENCE_SOURCES.has(sourceType);
  return sourceType === filter;
}

// ── Source labels & badge classes ─────────────────────────────────────

const SOURCE_LABELS: Record<string, string> = {
  eulaw: 'EU Law',
  rasff: 'RASFF',
  fsa_uk: 'UK FSA',
  openfda: 'US FDA',
  efsa: 'EFSA',
  cfia: 'CFIA',
  fsanz: 'FSANZ',
  fsis: 'USDA FSIS',
  rappelconso: 'France',
  lebensmittelwarnung: 'Germany',
  nvwa: 'Netherlands',
  favv: 'Belgium',
  favv_news: 'Belgium FAVV',
  fsai: 'Ireland',
  aesan: 'Spain',
  ecdc: 'ECDC',
  sciensano: 'Sciensano',
};

const SOURCE_BADGE_CLASS: Record<string, string> = {
  eulaw: 'source-badge--eulaw',
  rasff: 'source-badge--rasff',
  fsa_uk: 'source-badge--fsa',
  openfda: 'source-badge--fda',
  efsa: 'source-badge--efsa',
  cfia: 'source-badge--cfia',
  fsanz: 'source-badge--fsanz',
  fsis: 'source-badge--fsis',
  rappelconso: 'source-badge--rappelconso',
  lebensmittelwarnung: 'source-badge--lmw',
  nvwa: 'source-badge--nvwa',
  favv: 'source-badge--favv',
  favv_news: 'source-badge--favv',
  fsai: 'source-badge--fsai',
  aesan: 'source-badge--aesan',
  ecdc: 'source-badge--ecdc',
  sciensano: 'source-badge--sciensano',
};

// Alert type → consistent badge class (not tied to severity)
const ALERT_TYPE_CLASS: Record<string, string> = {
  recall: 'rasff-badge--alert',
  allergy: 'rasff-badge--info-att',
  action: 'rasff-badge--border',
  publication: 'rasff-badge--default',
};

// ── Data fetching ────────────────────────────────────────────────────

const baseUrl = API_BASE_URL;
const RASFF_PORTAL = 'https://webgate.ec.europa.eu/rasff-window/screen/notification';

async function fetchEuLaw(): Promise<EuLawItem[]> {
  const res = await fetch(`${baseUrl}/api/news_eurlex?take=100`, { cache: 'no-store' });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json() as { ok: boolean; news: EuLawItem[] };
  return data.news;
}

async function fetchRasff(): Promise<RasffItem[]> {
  const res = await fetch(`${baseUrl}/api/rasff-notifications`, { cache: 'no-store' });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json() as { ok: boolean; notifications: RasffItem[] };
  return data.notifications;
}

async function fetchFoodAlerts(): Promise<FoodAlertItem[]> {
  const res = await fetch(`${baseUrl}/api/food-alerts?days=365`, { cache: 'no-store' });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json() as { ok: boolean; alerts: FoodAlertItem[] };
  return data.alerts;
}

export async function fetchPipeline(): Promise<PipelineItem[]> {
  const res = await fetch(`${baseUrl}/api/legislation/pipeline?days=365&relevance=all`, { cache: 'no-store' });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json() as { ok: boolean; dossiers: PipelineItem[] };
  return data.dossiers;
}

export async function fetchDossier(key: string): Promise<PipelineDossier> {
  const res = await fetch(`${baseUrl}/api/legislation/dossier/${encodeURIComponent(key)}`, { cache: 'no-store' });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json() as { ok: boolean; dossier: PipelineDossier };
  return data.dossier;
}

// ── Pipeline stage helpers ───────────────────────────────────────────

export const STAGE_LABELS: Record<string, string> = {
  feedback_open: 'Feedback open',
  tabled: 'Tabled',
  voted_positive: 'Voted · positive',
  voted_negative: 'Voted · negative',
  no_opinion: 'No opinion',
  scrutiny: 'EP/Council scrutiny',
  adopted: 'Adopted',
  published: 'Published',
  applies: 'Applies',
  withdrawn: 'Withdrawn',
  in_progress: 'In progress',
};

const STAGE_CLASS: Record<string, string> = {
  feedback_open: 'stage-badge--early',
  tabled: 'stage-badge--early',
  voted_positive: 'stage-badge--voted',
  voted_negative: 'stage-badge--stopped',
  no_opinion: 'stage-badge--early',
  scrutiny: 'stage-badge--voted',
  adopted: 'stage-badge--late',
  published: 'stage-badge--late',
  applies: 'stage-badge--late',
  withdrawn: 'stage-badge--stopped',
  in_progress: 'stage-badge--early',
};

// Position of a stage on the 7-step strip; -1 = off the strip (withdrawn / unknown)
const STAGE_STEP: Record<string, number> = {
  feedback_open: 0, tabled: 1, voted_positive: 2, voted_negative: 2, no_opinion: 2,
  scrutiny: 3, adopted: 4, published: 5, applies: 6,
};
const STRIP_STEPS = ['Feedback', 'Tabled', 'Committee vote', 'Scrutiny', 'Adopted', 'Published', 'Applies'];

export function StageBadge({ stage }: { stage: string }) {
  return (
    <span className={`stage-badge ${STAGE_CLASS[stage] || 'stage-badge--early'}`}>
      {STAGE_LABELS[stage] || stage}
    </span>
  );
}

export function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const target = new Date(iso);
  if (isNaN(target.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86400000);
}

/** Pipeline items that matter to every operator get their own card; the
 *  single-applicant authorisations (one novel food, one PET recycling
 *  process, …) voted at the same meeting collapse into one grouped card. */
function groupSingleApplicant(items: PipelineItem[]): PipelineGroup[] {
  const groups = new Map<string, PipelineGroup>();
  for (const it of items) {
    const code = it.meeting_code || 'no-meeting';
    const g = groups.get(code) || { meeting_code: code, meeting_date: it.meeting_date, stage: it.stage, items: [] };
    g.items.push(it);
    if (!g.meeting_date && it.meeting_date) g.meeting_date = it.meeting_date;
    groups.set(code, g);
  }
  return Array.from(groups.values());
}

// ── Date parsing helpers ─────────────────────────────────────────────

function parseDDMMYYYY(dateStr: string | null): Date {
  if (!dateStr) return new Date(0);
  const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return new Date(0);
  return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
}

// ── Component ────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

type EuLawView = 'all' | 'pipeline' | 'published';
const EU_LAW_VIEWS: Array<[EuLawView, string]> = [
  ['all', 'All EU law'],
  ['pipeline', 'In the pipeline'],
  ['published', 'Published'],
];

export default function NewsPage() {
  usePageMeta({ title: 'Food safety news and recalls', description: 'Food recalls and alerts from authorities in Europe, the UK, the US, Canada, Australia and New Zealand, plus EU law updates, in English.' });
  // Deep links from the weekly digest: /news?tab=eulaw&dossier=CR:115270
  const params = new URLSearchParams(window.location.search);
  const tabParam = params.get('tab');
  const initialTab: SourceFilter = SOURCE_TABS.some(t => t.key === tabParam) ? (tabParam as SourceFilter) : 'all';
  const focusKey = params.get('dossier');

  const [activeTab, setActiveTab] = useState<SourceFilter>(initialTab);
  const [euLawView, setEuLawView] = useState<EuLawView>(focusKey ? 'pipeline' : 'all');
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const focused = useRef(false);

  const euLawQuery = useQuery<EuLawItem[], Error>(['allEuLaw'], fetchEuLaw);
  const rasffQuery = useQuery<RasffItem[], Error>(['allRasff'], fetchRasff);
  const alertsQuery = useQuery<FoodAlertItem[], Error>(['allFoodAlerts'], fetchFoodAlerts);
  // The pipeline is optional: an error here must not blank the whole page.
  const pipelineQuery = useQuery<PipelineItem[], Error>(['legislationPipeline'], fetchPipeline, { retry: 1 });

  const isLoading = euLawQuery.isLoading || rasffQuery.isLoading || alertsQuery.isLoading;
  const isError = euLawQuery.isError || rasffQuery.isError || alertsQuery.isError;
  const errorMsg = euLawQuery.error?.message || rasffQuery.error?.message || alertsQuery.error?.message || '';

  // Normalize into unified items
  const unified: UnifiedItem[] = [];

  if (pipelineQuery.data) {
    const general = pipelineQuery.data.filter(d => d.relevance !== 'single_applicant' && d.relevance !== 'out_of_scope');
    const single = pipelineQuery.data.filter(d => d.relevance === 'single_applicant');
    for (const d of general) {
      unified.push({
        id: `pipeline-${d.dossier_key}`,
        sourceType: 'eulaw',
        title: d.title || d.title_official || 'Untitled draft act',
        sortDate: d.last_event_at ? new Date(d.last_event_at) : new Date(0),
        pipeline: d,
      });
    }
    for (const g of groupSingleApplicant(single)) {
      const latest = g.items.reduce((m, it) => (it.last_event_at && it.last_event_at > m ? it.last_event_at : m), '');
      unified.push({
        id: `pipeline-group-${g.meeting_code}`,
        sourceType: 'eulaw',
        title: `${g.items.length} single-applicant authorisation${g.items.length === 1 ? '' : 's'}`,
        sortDate: latest ? new Date(latest) : new Date(0),
        pipelineGroup: g,
      });
    }
  }

  if (euLawQuery.data) {
    for (const item of euLawQuery.data) {
      unified.push({
        id: `eulaw-${item.id}`,
        sourceType: 'eulaw',
        title: item.post_title || 'Untitled',
        sortDate: parseDDMMYYYY(item.published_date) > new Date(1970, 0, 2)
          ? parseDDMMYYYY(item.published_date)
          : parseDDMMYYYY(item.date_of_document),
        eulaw: item,
      });
    }
  }

  if (rasffQuery.data) {
    for (const item of rasffQuery.data) {
      unified.push({
        id: `rasff-${item.rasff_id}`,
        sourceType: 'rasff',
        title: item.subject || 'Untitled',
        sortDate: item.ec_validation_date ? new Date(item.ec_validation_date) : new Date(0),
        rasff: item,
      });
    }
  }

  if (alertsQuery.data) {
    for (const item of alertsQuery.data) {
      unified.push({
        id: `alert-${item.source}-${item.id}`,
        sourceType: item.source,
        title: item.title || 'Untitled',
        sortDate: item.published_date ? new Date(item.published_date) : new Date(0),
        alert: item,
      });
    }
  }

  // Sort by date descending
  unified.sort((a, b) => b.sortDate.getTime() - a.sortDate.getTime());

  // Filter by active tab (+ the EU Law sub-filter: pipeline vs published)
  const filtered = unified.filter(u => {
    if (!matchesFilter(u.sourceType, activeTab)) return false;
    if (activeTab !== 'eulaw' || euLawView === 'all') return true;
    const isPipeline = Boolean(u.pipeline || u.pipelineGroup);
    return euLawView === 'pipeline' ? isPipeline : !isPipeline;
  });

  // Open the dossier named in the URL once the data is in
  useEffect(() => {
    if (!focusKey || focused.current || !pipelineQuery.data) return;
    const idx = filtered.findIndex(u => u.pipeline?.dossier_key === focusKey);
    if (idx >= 0) {
      focused.current = true;
      setPage(Math.floor(idx / PAGE_SIZE));
      setOpenIdx(idx % PAGE_SIZE);
    }
  }, [focusKey, pipelineQuery.data, filtered.length]);

  // Paginate — rendering all ~700 rows at once made a 28,000px page
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const goToPage = (p: number) => {
    setPage(p);
    setOpenIdx(null);
    window.scrollTo({ top: 0 });
  };

  return (
    <section className="news-page">
      <h1>Food Safety News</h1>

      <IncidentBanner />

      {/* Tab bar */}
      <div className="news-page__tabs">
        {SOURCE_TABS.map(tab => (
          <button
            key={tab.key}
            className={`news-page__tab ${activeTab === tab.key ? 'news-page__tab--active' : ''}`}
            onClick={() => { setActiveTab(tab.key); setOpenIdx(null); setPage(0); }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'eulaw' && (
        <div className="news-page__subfilter" role="group" aria-label="EU Law view">
          {EU_LAW_VIEWS.map(([key, label]) => (
            <button
              key={key}
              className={`news-page__chip ${euLawView === key ? 'news-page__chip--active' : ''}`}
              onClick={() => { setEuLawView(key); setOpenIdx(null); setPage(0); }}
            >
              {label}
            </button>
          ))}
          {pipelineQuery.isError && (
            <span className="news-page__subfilter-note">Pipeline data unavailable right now.</span>
          )}
          <RouterLink className="news-page__subfilter-note news-page__pipeline-link" to="/legislation/pipeline">
            All upcoming legislation →
          </RouterLink>
        </div>
      )}

      {isLoading && <LoadingSkeleton rows={8} title={false} />}
      {isError && <div className="error-state">Error: {errorMsg}</div>}

      {!isLoading && !isError && filtered.length === 0 && (
        <div className="loading-state">No news items found.</div>
      )}

      <ul className="feed-list">
        {paginated.map((item, i) => {
          const isOpen = openIdx === i;
          const riskAttr = item.rasff?.risk_decision?.toLowerCase() || item.alert?.severity?.toLowerCase() || undefined;
          return (
            <li key={item.id} className={`feed-item ${isOpen ? 'feed-item--open' : ''}`} data-risk={riskAttr}>
              {item.sourceType === 'eulaw' && item.eulaw && (
                <EuLawCard item={item.eulaw} isOpen={isOpen} onToggle={() => setOpenIdx(isOpen ? null : i)} />
              )}
              {item.pipeline && (
                <PipelineCard item={item.pipeline} isOpen={isOpen} onToggle={() => setOpenIdx(isOpen ? null : i)} />
              )}
              {item.pipelineGroup && (
                <PipelineGroupCard group={item.pipelineGroup} isOpen={isOpen} onToggle={() => setOpenIdx(isOpen ? null : i)} />
              )}
              {item.sourceType === 'rasff' && item.rasff && (
                <RasffCard item={item.rasff} isOpen={isOpen} onToggle={() => setOpenIdx(isOpen ? null : i)} />
              )}
              {(ALERT_SOURCES.has(item.sourceType) || item.sourceType === 'efsa') && item.alert && (
                <AlertCard item={item.alert} isOpen={isOpen} onToggle={() => setOpenIdx(isOpen ? null : i)} />
              )}
            </li>
          );
        })}
      </ul>

      {!isLoading && !isError && totalPages > 1 && (
        <div className="rasff-deepdive__pagination">
          <button className="rasff-dash__tf-btn" onClick={() => goToPage(0)} disabled={page === 0}>«</button>
          <button className="rasff-dash__tf-btn" onClick={() => goToPage(Math.max(0, page - 1))} disabled={page === 0}>‹ Prev</button>
          <span className="rasff-deepdive__pagination-info">
            Page {page + 1} of {totalPages}
            <span style={{ color: 'var(--color-text-secondary)', marginLeft: 8 }}>
              ({filtered.length} items)
            </span>
          </span>
          <button className="rasff-dash__tf-btn" onClick={() => goToPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}>Next ›</button>
          <button className="rasff-dash__tf-btn" onClick={() => goToPage(totalPages - 1)} disabled={page >= totalPages - 1}>»</button>
        </div>
      )}
    </section>
  );
}

// ── EU Law Card ──────────────────────────────────────────────────────

function EuLawCard({ item, isOpen, onToggle }: {
  item: EuLawItem; isOpen: boolean; onToggle: () => void;
}) {
  return (
    <>
      <button className="feed-item__btn" onClick={onToggle} aria-expanded={isOpen}>
        <span className="source-badge source-badge--eulaw">EU Law</span>
        <ImpactBadge level={item.impact_level} />
        <DocTypeBadge type={item.document_type} />
        <span className="feed-item__title">{item.post_title}</span>
        <span className="feed-item__date">
          {formatDDMMYYYY(item.published_date) || formatDDMMYYYY(item.date_of_document)}
        </span>
        <span className={`feed-item__chevron ${isOpen ? 'feed-item__chevron--open' : ''}`} aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="feed-item__content">
          {item.executive_summary && (
            <div className="feed-item__summary">
              <strong>Executive Summary:</strong> {item.executive_summary}
            </div>
          )}
          {item.action_required && (
            <div className="feed-item__action">
              <strong>Action Required:</strong> {item.action_required}
            </div>
          )}
          {item.key_dates && item.key_dates.length > 0 && (
            <div className="feed-item__key-dates">
              <strong>Key Dates:</strong>
              <ul>
                {item.key_dates.map((kd, j) => (
                  <li key={j}>
                    <span className="feed-item__kd-date">{kd.date}</span>
                    <span className="feed-item__kd-desc">{kd.description}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {item.affected_products && item.affected_products.length > 0 && (
            <div className="feed-item__products">
              <strong>Affected Products:</strong>{' '}
              {item.affected_products.map((p, j) => (
                <span key={j} className="feed-item__product-tag">{p}</span>
              ))}
            </div>
          )}
          {item.post_content && (
            <div className="feed-item__full-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {cleanPostContent(item.post_content)}
              </ReactMarkdown>
            </div>
          )}
          <div className="feed-item__links">
            {item.post_link && (
              <a href={item.post_link} target="_blank" rel="noopener noreferrer">
                Full document
              </a>
            )}
            {item.regulation?.url_legislation && (
              <a href={item.regulation.url_legislation} target="_blank" rel="noopener noreferrer">
                Legislation text
              </a>
            )}
            {item.celex_number && (
              <span className="feed-item__celex">CELEX: {item.celex_number}</span>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ── Pipeline (draft act) Card ────────────────────────────────────────

function StageStrip({ stage }: { stage: string }) {
  const current = STAGE_STEP[stage] ?? -1;
  return (
    <ol className={`stage-strip ${stage === 'withdrawn' ? 'stage-strip--stopped' : ''}`} aria-label="Legislative stage">
      {STRIP_STEPS.map((label, i) => {
        const cls = i < current ? 'stage-strip__step--done' : i === current ? 'stage-strip__step--now' : '';
        return (
          <li key={label} className={`stage-strip__step ${cls}`}>
            <span className="stage-strip__dot" aria-hidden="true" />
            <span className="stage-strip__label">{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

function sourceLabel(src: PipelineSource): string {
  switch (src.type) {
    case 'D': return `Draft text (v${src.version})`;
    case 'V': return 'Voting sheet';
    case 'S': return 'Meeting summary';
    case 'A': return 'Meeting agenda';
    default: return src.type;
  }
}

function PipelineCard({ item, isOpen, onToggle }: {
  item: PipelineItem; isOpen: boolean; onToggle: () => void;
}) {
  // The briefing, key dates and documents load the first time the card opens.
  const detail = useQuery<PipelineDossier, Error>(
    ['legislationDossier', item.dossier_key],
    () => fetchDossier(item.dossier_key),
    { enabled: isOpen, staleTime: 5 * 60 * 1000, retry: 1 },
  );
  const days = daysUntil(item.expected_application);
  const headerDate = item.stage_date || (item.last_event_at ? item.last_event_at.slice(0, 10) : null);

  return (
    <>
      <button className="feed-item__btn" onClick={onToggle} aria-expanded={isOpen}>
        <span className="source-badge source-badge--eulaw">EU Law</span>
        <StageBadge stage={item.stage} />
        <ImpactBadge level={item.impact_level} />
        <DocTypeBadge type={item.act_type} />
        <span className="feed-item__title">{item.title || item.title_official}</span>
        <span className="feed-item__date">{formatISODate(headerDate)}</span>
        <span className={`feed-item__chevron ${isOpen ? 'feed-item__chevron--open' : ''}`} aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="feed-item__content">
          <div className="pipeline-card__meta">
            {item.section_label && <span>{item.section_label}</span>}
            {item.meeting_date && <span>Committee meeting {formatISODate(item.meeting_date)}</span>}
            {item.vote_opinion && <span>{STAGE_LABELS[item.vote_opinion] || item.vote_opinion}</span>}
            {item.basic_act_ref && <span>Amends {item.basic_act_ref}</span>}
            {item.plan_ref && <span>SANTE PLAN/{item.plan_ref}</span>}
            {item.hazard && <span>Hazard: {item.hazard.name}</span>}
          </div>

          <StageStrip stage={item.stage} />
          <ExplainerButton station={stationOf(item.stage)} className="legis-explainer-link legis-explainer-link--inline">
            What happens at each stage?
          </ExplainerButton>

          <div className="pipeline-card__applies">
            {item.expected_application ? (
              <>
                <strong>Applies from {formatISODate(item.expected_application)}</strong>
                <span className="pipeline-card__applies-note">
                  {item.application_confirmed ? 'confirmed in the Official Journal' : 'expected, from the draft text; final on OJ publication'}
                  {days !== null && days >= 0 ? ` · in ${days} day${days === 1 ? '' : 's'}` : ''}
                </span>
              </>
            ) : (
              <span className="pipeline-card__applies-note">Application date not yet fixed in the draft.</span>
            )}
          </div>

          {detail.isLoading && (
            <div className="feed-item__summary"><em>Loading the briefing and documents…</em></div>
          )}
          {detail.isError && (
            <div className="feed-item__summary"><em>Could not load the briefing and documents. Close and reopen to retry.</em></div>
          )}
          {detail.data && <PipelineDetail dossier={detail.data} />}
        </div>
      )}
    </>
  );
}

/** The part of an opened pipeline card that comes from the dossier endpoint. */
function PipelineDetail({ dossier }: { dossier: PipelineDossier }) {
  const sources = dossier.sources || [];
  const draftFiles = sources
    .filter(s => s.type === 'D' && s.files && s.files.length > 0)
    .slice(-1)
    .flatMap(s => (s.files || []).map(f => ({ ...f, version: s.version })));
  const latestDraft = sources.filter(x => x.type === 'D').reduce((m, x) => Math.max(m, x.version), 0);
  const registerDocs = sources.filter(s => s.type !== 'D' || s.version === latestDraft);

  return (
    <>
      {dossier.executive_summary && (
        <div className="feed-item__summary">
          <strong>Executive Summary:</strong> {dossier.executive_summary}
        </div>
      )}
      {dossier.action_required && (
        <div className="feed-item__action">
          <strong>Action Required:</strong> {dossier.action_required}
        </div>
      )}
      {dossier.key_dates && dossier.key_dates.length > 0 && (
        <div className="feed-item__key-dates">
          <strong>Key Dates:</strong>
          <ul>
            {dossier.key_dates.map((kd, j) => (
              <li key={j}>
                <span className="feed-item__kd-date">{formatISODate(kd.date) || kd.date}</span>
                <span className="feed-item__kd-desc">
                  {kd.description}
                  {kd.confidence && kd.confidence !== 'high' ? ` (${kd.confidence} confidence)` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {dossier.affected_products && dossier.affected_products.length > 0 && (
        <div className="feed-item__products">
          <strong>Affected Products:</strong>{' '}
          {dossier.affected_products.map((p, j) => (
            <span key={j} className="feed-item__product-tag">{p}</span>
          ))}
        </div>
      )}
      {dossier.post_content ? (
        <div className="feed-item__full-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {cleanPostContent(dossier.post_content)}
          </ReactMarkdown>
        </div>
      ) : (
        <div className="feed-item__summary">
          <em>No briefing yet for this draft (the English text has not been analysed).</em>
        </div>
      )}
      <div className="feed-item__links">
        {draftFiles.map((f, j) => (
          <a key={j} href={f.url} target="_blank" rel="noopener noreferrer">
            {f.role === 'annex' ? 'Annex' : f.role === 'act' ? 'Draft text' : 'Attachment'} ({f.language || 'EN'}, v{f.version})
          </a>
        ))}
        {registerDocs.map((src, j) => (
          <a key={`r${j}`} href={src.url} target="_blank" rel="noopener noreferrer">
            {sourceLabel(src)}
          </a>
        ))}
        {dossier.celex_number && (
          <span className="feed-item__celex">CELEX: {dossier.celex_number}</span>
        )}
        <span className="feed-item__celex">Register: {dossier.register_refs.map(r => `D${r}`).join(', ')}</span>
      </div>
    </>
  );
}

function PipelineGroupCard({ group, isOpen, onToggle }: {
  group: PipelineGroup; isOpen: boolean; onToggle: () => void;
}) {
  const n = group.items.length;
  return (
    <>
      <button className="feed-item__btn" onClick={onToggle} aria-expanded={isOpen}>
        <span className="source-badge source-badge--eulaw">EU Law</span>
        <StageBadge stage={group.stage} />
        <span className="feed-item__title">
          {n} single-applicant authorisation{n === 1 ? '' : 's'} (novel foods, recycling processes, specifications)
        </span>
        <span className="feed-item__date">{formatISODate(group.meeting_date)}</span>
        <span className={`feed-item__chevron ${isOpen ? 'feed-item__chevron--open' : ''}`} aria-hidden="true" />
      </button>
      {isOpen && (
        <div className="feed-item__content">
          <div className="feed-item__summary">
            Acts that concern one applicant or one product, tabled or voted at the committee meeting of {formatISODate(group.meeting_date)}. They rarely change obligations for other operators.
          </div>
          <ul className="pipeline-group__list">
            {group.items.map(it => (
              <li key={it.dossier_key}>
                <StageBadge stage={it.stage} />{' '}
                {it.draft_url ? (
                  <a href={it.draft_url} target="_blank" rel="noopener noreferrer">{it.title || it.title_official}</a>
                ) : (
                  <span>{it.title || it.title_official}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

// ── RASFF Card ───────────────────────────────────────────────────────

function RasffCard({ item, isOpen, onToggle }: {
  item: RasffItem; isOpen: boolean; onToggle: () => void;
}) {
  const { label, className } = getClassificationInfo(item.classification);

  return (
    <>
      <button className="feed-item__btn" onClick={onToggle} aria-expanded={isOpen}>
        <span className="source-badge source-badge--rasff">RASFF</span>
        <span className={`rasff-badge ${className}`}>{label}</span>
        <a
          href={`${RASFF_PORTAL}/${item.notif_id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="rasff-ref"
          onClick={(e) => e.stopPropagation()}
        >{item.reference}</a>
        <span className="feed-item__title">{item.subject}</span>
        <span className="feed-item__date">{formatISODate(item.ec_validation_date)}</span>
        <span className={`feed-item__chevron ${isOpen ? 'feed-item__chevron--open' : ''}`} aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="rasff-details">
          <div className="rasff-details__grid">
            <div className="rasff-detail">
              <span className="rasff-detail__label">Notifying country</span>
              <span className="rasff-detail__value">{item.notifying_country ?? 'N/A'}</span>
            </div>
            <div className="rasff-detail">
              <span className="rasff-detail__label">Product category</span>
              <span className="rasff-detail__value">{item.product_category ?? 'N/A'}</span>
            </div>
            <div className="rasff-detail">
              <span className="rasff-detail__label">Risk decision</span>
              <span className="rasff-detail__value">{item.risk_decision ?? 'N/A'}</span>
            </div>
            {item.dim_hazards && item.dim_hazards.hazardname !== 'Unknown Hazard Category' && (
              <div className="rasff-detail">
                <span className="rasff-detail__label">Hazard category</span>
                <span className="rasff-detail__value">{item.dim_hazards.hazardname}</span>
              </div>
            )}
          </div>
          {item.hazard_names && (
            <div className="rasff-detail rasff-detail--full">
              <span className="rasff-detail__label">Hazards</span>
              <span className="rasff-detail__value rasff-detail__hazards">{item.hazard_names}</span>
            </div>
          )}
          <RelatedKnowledge notifId={item.notif_id} />
          <div className="rasff-portal-link">
            <a
              href={`${RASFF_PORTAL}/${item.notif_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              View on RASFF Portal &rarr;
            </a>
          </div>
        </div>
      )}
    </>
  );
}

// ── Food Alert / EFSA Card ──────────────────────────────────────────

function AlertCard({ item, isOpen, onToggle }: {
  item: FoodAlertItem; isOpen: boolean; onToggle: () => void;
}) {
  const sourceLabel = SOURCE_LABELS[item.source] || item.source;
  const badgeClass = SOURCE_BADGE_CLASS[item.source] || 'source-badge--fda';
  // RappelConso recall events fold several fiches into one row; each member
  // fiche is listed here so every underlying notice stays one click away.
  // The ETL keeps "(N notices)" in the stored title for text-only consumers
  // (digest, social); on the page we show it as a badge instead so the cue
  // survives title truncation on narrow viewports.
  const notices = Array.isArray(item.hazards) ? item.hazards.filter(h => h.type === 'notice') : [];
  const displayTitle = notices.length > 1
    ? (item.title || '').replace(/\s*\(\d+ notices\)\s*$/, '')
    : item.title;

  return (
    <>
      <button className="feed-item__btn" onClick={onToggle} aria-expanded={isOpen}>
        <span className={`source-badge ${badgeClass}`}>{sourceLabel}</span>
        {item.alert_type && (
          <span className={`rasff-badge ${ALERT_TYPE_CLASS[item.alert_type] || 'rasff-badge--default'}`}>
            {item.alert_type}
          </span>
        )}
        {notices.length > 1 && (
          <span className="rasff-badge rasff-badge--default" title={`${notices.length} RappelConso notices grouped into one recall event`}>
            {notices.length} notices
          </span>
        )}
        <span className="feed-item__title">{displayTitle}</span>
        <span className="feed-item__date">{formatISODate(item.published_date)}</span>
        <span className={`feed-item__chevron ${isOpen ? 'feed-item__chevron--open' : ''}`} aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="rasff-details">
          <div className="rasff-details__grid">
            {item.severity && (
              <div className="rasff-detail">
                <span className="rasff-detail__label">Severity</span>
                <span className="rasff-detail__value">{item.severity}</span>
              </div>
            )}
            {item.countries && item.countries.length > 0 && (
              <div className="rasff-detail">
                <span className="rasff-detail__label">Countries</span>
                <span className="rasff-detail__value">{item.countries.join(', ')}</span>
              </div>
            )}
            {item.hazard_name && item.hazard_name !== 'Unknown Hazard Category' && (
              <div className="rasff-detail">
                <span className="rasff-detail__label">Hazard category</span>
                <span className="rasff-detail__value">{item.hazard_name}</span>
              </div>
            )}
            <div className="rasff-detail">
              <span className="rasff-detail__label">Source</span>
              <span className="rasff-detail__value">{sourceLabel}</span>
            </div>
          </div>
          {item.description && (
            <div className="rasff-detail rasff-detail--full">
              <span className="rasff-detail__label">Description</span>
              <span className="rasff-detail__value">{item.description}</span>
            </div>
          )}
          {item.products && item.products.length > 0 && (
            <div className="rasff-detail rasff-detail--full">
              <span className="rasff-detail__label">Products</span>
              <span className="rasff-detail__value">
                {item.products.map((p, j) => (
                  <span key={j} className="feed-item__product-tag">{typeof p === 'string' ? p : JSON.stringify(p)}</span>
                ))}
              </span>
            </div>
          )}
          {notices.length > 0 && (
            <div className="rasff-detail rasff-detail--full">
              <span className="rasff-detail__label">Notices ({notices.length})</span>
              <ul className="feed-item__notices">
                {notices.map((n, j) => (
                  <li key={n.reference || j}>
                    {n.url ? (
                      <a href={n.url} target="_blank" rel="noopener noreferrer">{n.text}</a>
                    ) : n.text}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {item.url && (
            <div className="rasff-portal-link">
              <a href={item.url} target="_blank" rel="noopener noreferrer">
                View source &rarr;
              </a>
            </div>
          )}
        </div>
      )}
    </>
  );
}
